import Tag from 'components/Tag'
import AndroidDownloadOptions from 'components/download-page/AndroidDownloadOptions'
import BasePlatformFeatureList from 'components/download-page/BasePlatformFeatureList'
import BaseHeader from 'components/download-page/BasePlatformHeader'
import DocumentationLinks from 'components/download-page/DocumentationLinks'
import IosDownloadOptions from 'components/download-page/IosDownloadOptions'
import PlatformSelector from 'components/download-page/PlatformSelector'
import PlatformTitle from 'components/download-page/PlatformTitle'
import { graphql, useStaticQuery } from 'gatsby'
import { StaticImage } from 'gatsby-plugin-image'
import { baseImageStyle } from 'pages/download'
import { Dispatch, SetStateAction } from 'react'
import { Flex } from 'theme-ui'
import { AvailablePlatforms } from 'utils/downloadPageUtils'
import getEnglishStrings from 'utils/getEnglishStrings'

export default function MobilePlatform({
  platformName,
  setPlatform,
}: {
  platformName: AvailablePlatforms
  setPlatform: Dispatch<SetStateAction<AvailablePlatforms>>
}): JSX.Element {
  const translations = useStaticQuery<Queries.MobilePlatformQuery>(graphql`
    query MobilePlatform {
      directus {
        download_page {
          strings {
            languages_code {
              code
            }
            windscribe_for_android
            windscribe_for_ios
            servers_in_over
            six_different_protocols
            customizable_in_app
          }
        }
        general_translations {
          strings {
            languages_code {
              code
            }
            mobile
            download
            or
          }
        }
      }
    }
  `)
  const downloadPageStrings = getEnglishStrings(translations.directus.download_page?.strings)
  const generalTranslationStrings = getEnglishStrings(
    translations.directus.general_translations?.strings,
  )

  return (
    <BaseHeader>
      <Tag title={generalTranslationStrings?.mobile ?? ''} />
      <PlatformSelector
        platformName={platformName}
        setPlatform={setPlatform}
        availablePlatforms={['iOS', 'Android']}
      />
      <Flex
        sx={{
          justifyContent: 'space-between',
          flexDirection: ['column-reverse', 'row', 'row'],
          alignItems: ['center', 'flex-start'],
          width: '100%',
        }}
      >
        <Flex
          sx={{
            flexDirection: 'column',
            gap: ['3.2rem', '4.8rem'],
            alignItems: ['center', 'center', 'flex-start'],
            minWidth: ['auto', '38.5rem'],
          }}
        >
          <PlatformTitle
            title={
              {
                Android: downloadPageStrings?.windscribe_for_android ?? '',
                iOS: downloadPageStrings?.windscribe_for_ios ?? '',
              }[platformName]
            }
          />
          {/* Download Section */}
          <Flex sx={{ flexDirection: 'column', maxWidth: ['38.5rem', 'auto'], gap: '2.4rem' }}>
            {platformName === 'Android' ? <AndroidDownloadOptions /> : <IosDownloadOptions />}
          </Flex>
          <Flex sx={{ flexDirection: 'column', gap: '2.4rem' }}>
            <BasePlatformFeatureList
              featureList={[
                downloadPageStrings?.servers_in_over ?? '',
                downloadPageStrings?.six_different_protocols ?? '',
                downloadPageStrings?.customizable_in_app ?? '',
              ]}
            />
            <DocumentationLinks platformName={platformName} />
          </Flex>
        </Flex>
        <Flex sx={{ maxWidth: '45.8rem', flexShrink: 1, width: '100%', justifyContent: 'center' }}>
          {
            <>
              <StaticImage
                src="../../images/windscribe-mobile-android.png"
                alt={''}
                placeholder="blurred"
                loading="eager"
                sx={{
                  ...baseImageStyle,
                  display: platformName === 'Android' ? 'flex' : 'none',
                }}
                imgStyle={{
                  objectFit: 'scale-down',
                }}
              />
              <StaticImage
                src="../../images/windscribe-mobile-ios.png"
                alt={''}
                placeholder="blurred"
                loading="eager"
                sx={{
                  ...baseImageStyle,
                  display: platformName === 'iOS' ? 'flex' : 'none',
                }}
                imgStyle={{
                  objectFit: 'scale-down',
                }}
              />
            </>
          }
        </Flex>
      </Flex>
    </BaseHeader>
  )
}
